import ScreenMargin from '@/components/ScreenMargin';
import { ArrayElement, SortState } from '@/utils/typeHelpers';
import {
  ListPatientsWithStatusQuery,
  useDeletePatientMutation,
  useListPatientsWithStatusLazyQuery,
  useArchivePatientMutation,
} from '@/data/data';
import { Border, Color, FontFamily, FontSize, Padding } from '@/GlobalStyles';
import { IconButton, Text, Checkbox, Icon } from 'react-native-paper';
import { router } from 'expo-router';
import DataTable, { getOrderByClause } from '@/components/DataTable/DataTable';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useRefetchOnFocus } from '@/hooks/useRefetchOnFocus';
import DateUtils from '@/utils/DateUtils';
import { Alert, Pressable, StyleSheet, View, useWindowDimensions, Platform } from 'react-native';
import Button from '@/components/Button';
import TextInput from '@/components/Form/TextInput';
import { Image } from 'expo-image';
import Formatter from '@/utils/Formatter';
import { useTranslation } from 'react-i18next';
import DossierStatusIndicator, { DossierStatus } from '@/components/Patient/DossierStatusIndicator';
import DossierStatusFilter from '@/components/Patient/DossierStatusFilter';
import { getPatientDossierStatus, filterPatientsByStatus } from '@/utils/patientStatusUtils';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Menu from '@/components/Menu/Menu';
import MenuItem from '@/components/Menu/MenuItem';
import MenuDivider from '@/components/Menu/MenuDivider';

const PatientsIndexPage = () => {
  const [loadPatients, { loading, data, refetch }] = useListPatientsWithStatusLazyQuery();
  const [sortState, setSortState] = useState<
    SortState<ArrayElement<ListPatientsWithStatusQuery['patient']>>
  >({
    key: 'updatedAt',
    sort: 'descending',
  });
  const [lastChange, setLastChange] = useState('');
  const [search, setSearch] = useState<string | undefined>();
  const [selectedStatuses, setSelectedStatuses] = useState<DossierStatus[]>([]);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  const { t } = useTranslation();
  const { width: screenWidth } = useWindowDimensions();
  const isMobile = screenWidth < 768;

  // Charger les préférences de filtre au montage
  useEffect(() => {
    AsyncStorage.getItem('patientStatusFilter').then((value) => {
      if (value) {
        setSelectedStatuses(JSON.parse(value));
      }
    });
  }, []);

  // Sauvegarder les préférences de filtre
  useEffect(() => {
    AsyncStorage.setItem('patientStatusFilter', JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useRefetchOnFocus(
    useCallback(
      () =>
        refetch({
          orderBy: getOrderByClause(sortState),
          where:
            search !== undefined && search !== ''
              ? {
                  _or: [
                    {
                      nom: {
                        _ilike: `%${search}%`,
                      },
                    },
                    {
                      prenom: {
                        _ilike: `%${search}%`,
                      },
                    },
                  ],
                }
              : undefined,
        }),
      [sortState, lastChange]
    )
  );

  useEffect(() => {
    loadPatients({
      variables: {
        orderBy: getOrderByClause(sortState),
        where:
          search !== undefined && search !== ''
            ? {
                _or: [
                  {
                    nom: {
                      _ilike: `%${search}%`,
                    },
                  },
                  {
                    prenom: {
                      _ilike: `%${search}%`,
                    },
                  },
                ],
              }
            : undefined,
      },
    });
  }, [sortState, lastChange]);

  const [deletePatient] = useDeletePatientMutation();
  const [archivePatient] = useArchivePatientMutation();

  // Filtrer les patients par statut
  const filteredPatients = useMemo(() => {
    if (!data?.patient) return [];
    return filterPatientsByStatus(data.patient as any[], selectedStatuses);
  }, [data?.patient, selectedStatuses]);

  // Fonction pour basculer un statut
  const handleStatusToggle = (status: DossierStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  // Fonction pour archiver/désarchiver un patient
  const handleArchivePatient = async (patientId: string, isArchived: boolean) => {
    await archivePatient({
      variables: { id: patientId, isArchived },
      fetchPolicy: 'network-only',
    });
    setLastChange(new Date().toISOString());
    Alert.alert(t('Succès'), isArchived ? t('Patient archivé') : t('Patient désarchivé'));
  };

  // Fonction pour supprimer plusieurs patients
  const handleBulkDelete = () => {
    Alert.alert(
      t('Attention'),
      t('Êtes-vous sûr de vouloir supprimer {{count}} patient(s) ?', {
        count: selectedPatients.length,
      }),
      [
        {
          text: t('Annuler'),
          style: 'cancel',
        },
        {
          text: 'OK',
          onPress: async () => {
            for (const patientId of selectedPatients) {
              await deletePatient({
                variables: { id: patientId },
                fetchPolicy: 'network-only',
              });
            }
            setSelectedPatients([]);
            setSelectionMode(false);
            setLastChange(new Date().toISOString());
          },
        },
      ]
    );
  };

  // Fonction de rendu pour les cartes sur mobile
  const renderPatientCard = (
    item: ArrayElement<ListPatientsWithStatusQuery['patient']> & { loading?: boolean },
    numberOfColumns: number,
    onPress?: (item: any) => void
  ) => {
    const status = getPatientDossierStatus(item as any);
    const isArchived = (item as any).isArchived;

    const statusColors = {
      ORANGE: '#FF9800',
      VERT: '#4CAF50',
      GRIS: '#9E9E9E',
    };

    const statusLabels = {
      ORANGE: t('En cours'),
      VERT: t('Finalisé'),
      GRIS: t('Archivé'),
    };

    return (
      <View style={styles.cardContainer}>
        <Pressable
          onPress={() => !selectionMode && onPress && onPress(item)}
          style={[styles.card, item.loading && styles.cardLoading]}
        >
          {/* Badge de statut */}
          <View style={[styles.statusBadge, { backgroundColor: statusColors[status] }]} />

          {/* Contenu principal */}
          <View style={styles.cardContent}>
            <View style={styles.cardHeader}>
              <View style={styles.cardInfo}>
                <Text style={styles.cardTitle}>
                  {Formatter.FirstUpper(`${item.nom} ${item.prenom}`)}
                </Text>
                <Text style={styles.cardSubtitle}>{DateUtils.convertDate(item.birthday)}</Text>
                <Text style={[styles.cardStatus, { color: statusColors[status] }]}>
                  {statusLabels[status]}
                </Text>
              </View>

              {/* Actions */}
              <View style={styles.cardActions}>
                {selectionMode ? (
                  <Checkbox.Android
                    uncheckedColor={Color.colorGray_100}
                    background={Color.BlancCasse}
                    status={selectedPatients.includes(item.id) ? 'checked' : 'unchecked'}
                    onPress={() => {
                      setSelectedPatients((prev) =>
                        prev.includes(item.id)
                          ? prev.filter((id) => id !== item.id)
                          : [...prev, item.id]
                      );
                    }}
                  />
                ) : (
                  <Menu
                    isSimplePressable
                    anchor={
                      <IconButton
                        icon="dots-vertical"
                        iconColor={Color.colorGray_150}
                        size={24}
                        style={{ margin: 0 }}
                      />
                    }
                    posX={-17}
                  >
                    <MenuItem
                      title={t('Voir')}
                      onPress={() => router.push(`/patients/${item.id}`)}
                    />
                    <MenuItem
                      title={t('Envoyer le lien')}
                      onPress={() => router.push(`/patients/${item.id}/send-link`)}
                    />
                    <MenuItem
                      title={t('Modifier')}
                      onPress={() => router.push(`/patients/${item.id}/maj`)}
                    />
                    <MenuDivider />
                    <MenuItem
                      title={isArchived ? t('Désarchiver') : t('Archiver')}
                      onPress={() => {
                        handleArchivePatient(item.id, !isArchived);
                      }}
                    />
                    <MenuItem
                      title={t('Supprimer')}
                      textStyle={{ color: Color.error }}
                      onPress={() => {
                        Alert.alert(
                          t('Attention'),
                          t('Êtes-vous sûr de vouloir supprimer ce patient ?'),
                          [
                            {
                              text: t('Annuler'),
                              style: 'cancel',
                            },
                            {
                              text: 'OK',
                              onPress: async () => {
                                await deletePatient({
                                  variables: { id: item.id },
                                  fetchPolicy: 'network-only',
                                });
                                setLastChange(new Date().toISOString());
                              },
                            },
                          ]
                        );
                      }}
                    />
                  </Menu>
                )}
              </View>
            </View>
          </View>
        </Pressable>
      </View>
    );
  };

  return (
    <ScreenMargin>
      <DataTable<ArrayElement<ListPatientsWithStatusQuery['patient']>>
        forceListType={isMobile ? 'card' : 'list'}
        forceNumberOfColumns={isMobile ? 1 : undefined}
        renderCard={isMobile ? renderPatientCard : undefined}
        headerSideComponent={
          isMobile ? (
            // Menu pour mobile
            selectionMode ? (
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                <Text style={{ fontSize: 14, color: Color.MainNoir }}>
                  {selectedPatients.length} {t('sélectionné(s)')}
                </Text>
                <Menu
                  isSimplePressable
                  anchor={
                    <View style={{ padding: 8 }}>
                      <Icon source="dots-vertical" size={24} color={Color.MainBleu} />
                    </View>
                  }
                  posX={-17}
                >
                  <MenuItem
                    title={
                      selectedPatients.length === filteredPatients.length
                        ? t('Tout désélectionner')
                        : t('Tout sélectionner')
                    }
                    onPress={() => {
                      if (selectedPatients.length === filteredPatients.length) {
                        setSelectedPatients([]);
                      } else {
                        setSelectedPatients(filteredPatients.map((p) => p.id));
                      }
                    }}
                  />
                  {selectedPatients.length > 0 ? (
                    <>
                      <MenuDivider />
                      <MenuItem
                        title={t('Supprimer ({{count}})', { count: selectedPatients.length })}
                        textStyle={{ color: Color.error }}
                        onPress={handleBulkDelete}
                      />
                    </>
                  ) : null}
                  <MenuDivider />
                  <MenuItem
                    title={t('Annuler la sélection')}
                    onPress={() => {
                      setSelectionMode(false);
                      setSelectedPatients([]);
                    }}
                  />
                </Menu>
              </View>
            ) : (
              <Menu
                isSimplePressable
                anchor={
                  <View style={{ padding: 8 }}>
                    <Icon source="dots-vertical" size={24} color={Color.MainBleu} />
                  </View>
                }
                posX={-17}
              >
                <MenuItem
                  title={t('Ajouter un patient')}
                  onPress={() => router.push('/patients/ajouter')}
                />
                <MenuItem title={t('Mode sélection')} onPress={() => setSelectionMode(true)} />
              </Menu>
            )
          ) : (
            // Boutons pour desktop
            <View
              style={{
                flexDirection: 'row',
                gap: 8,
                alignItems: 'center',
              }}
            >
              {selectionMode ? (
                <>
                  {selectedPatients.length > 0 && (
                    <Button
                      mode="outlined"
                      onPress={handleBulkDelete}
                      style={{
                        borderColor: Color.error,
                      }}
                      textStyle={{
                        color: Color.error,
                        fontSize: 14,
                      }}
                      withShadow={false}
                    >
                      {t('Supprimer ({{count}})', { count: selectedPatients.length })}
                    </Button>
                  )}
                  <Button
                    withShadow={false}
                    mode="outlined"
                    textStyle={{ fontSize: 14 }}
                    onPress={() => {
                      if (selectedPatients.length === filteredPatients.length) {
                        setSelectedPatients([]);
                      } else {
                        setSelectedPatients(filteredPatients.map((p) => p.id));
                      }
                    }}
                  >
                    {selectedPatients.length === filteredPatients.length
                      ? t('Tout désélectionner')
                      : t('Tout sélectionner')}
                  </Button>
                  <Button
                    mode="outlined"
                    withShadow={false}
                    textStyle={{ fontSize: 14 }}
                    onPress={() => {
                      setSelectionMode(false);
                      setSelectedPatients([]);
                    }}
                  >
                    {t('Annuler')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    mode="contained"
                    withShadow={false}
                    textStyle={{ fontSize: 14 }}
                    onPress={() => setSelectionMode(true)}
                  >
                    {t('Sélectionner')}
                  </Button>
                  <Button
                    withShadow={false}
                    onPress={() => router.push('/patients/ajouter')}
                    style={{
                      alignSelf: 'flex-end',
                    }}
                    textStyle={{ fontSize: 14 }}
                  >
                    {t('Ajouter')}
                  </Button>
                </>
              )}
            </View>
          )
        }
        subHeaderComponent={
          <>
            <View style={{ flexDirection: 'row', marginTop: 12, justifyContent: 'space-between' }}>
              <TextInput
                name={'search'}
                onChangeValue={(text) => {
                  setSearch(text);
                  if (text === '' || text === undefined) {
                    setLastChange(new Date().toISOString());
                  }
                }}
                label={'Recherche'}
                placeholder={'Recherche par nom'}
                containerStyle={{ marginTop: 0, flex: 1 }}
              />
              <Pressable
                style={styles.iconContainer}
                onPress={() => setLastChange(new Date().toISOString())}
              >
                <Image
                  style={styles.icon}
                  contentFit="cover"
                  source={require('@assets/icones/loupe.png')}
                />
              </Pressable>
            </View>
            <DossierStatusFilter
              selectedStatuses={selectedStatuses}
              onStatusToggle={handleStatusToggle}
              onReset={() => setSelectedStatuses([])}
            />
          </>
        }
        dataTotalCount={10}
        title={t('Patients')}
        loading={loading}
        data={filteredPatients}
        sortByState={sortState}
        onHeaderPress={(item) => {
          setSortState({
            key: item.key,
            sort:
              sortState.key === item.key && sortState.sort === 'ascending'
                ? 'descending'
                : 'ascending',
          });
        }}
        keyExtractor={(item) => `patient_${item.id}`}
        headers={[
          ...(selectionMode
            ? [
                {
                  title: '',
                  key: 'select',
                  sortable: false,
                  style: { width: 32, minWidth: 32, maxWidth: 32, paddingRight: 4, paddingLeft: 4 },
                  headerValue: () => (
                    <Checkbox.Android
                      uncheckedColor={Color.colorGray_100}
                      background={Color.BlancCasse}
                      status={
                        filteredPatients.length > 0 &&
                        selectedPatients.length === filteredPatients.length
                          ? 'checked'
                          : selectedPatients.length > 0
                            ? 'indeterminate'
                            : 'unchecked'
                      }
                      onPress={() => {
                        if (selectedPatients.length === filteredPatients.length) {
                          setSelectedPatients([]);
                        } else {
                          setSelectedPatients(filteredPatients.map((p) => p.id));
                        }
                      }}
                      style={{ margin: 0, padding: 0 }}
                    />
                  ),
                  value: (item: any, index: number) => (
                    <Checkbox.Android
                      uncheckedColor={Color.colorGray_100}
                      background={Color.BlancCasse}
                      status={selectedPatients.includes(item.id) ? 'checked' : 'unchecked'}
                      onPress={(e: any) => {
                        const shiftKey = e?.nativeEvent?.shiftKey || false;

                        if (shiftKey && lastSelectedIndex !== null && lastSelectedIndex !== index) {
                          // Sélection multiple avec Shift
                          const start = Math.min(lastSelectedIndex, index);
                          const end = Math.max(lastSelectedIndex, index);
                          const rangeIds = filteredPatients.slice(start, end + 1).map((p) => p.id);

                          setSelectedPatients((prev) => {
                            const newSelection = new Set(prev);
                            rangeIds.forEach((id) => newSelection.add(id));
                            return Array.from(newSelection);
                          });
                        } else {
                          // Sélection simple
                          setSelectedPatients((prev) =>
                            prev.includes(item.id)
                              ? prev.filter((id) => id !== item.id)
                              : [...prev, item.id]
                          );
                        }

                        setLastSelectedIndex(index);
                      }}
                      style={{ margin: 0, padding: 0 }}
                    />
                  ),
                },
              ]
            : []),
          {
            title: '',
            key: 'status',
            sortable: false,
            style: { width: 24, minWidth: 24, maxWidth: 24, paddingRight: 4, paddingLeft: 8 },
            value: (item) => {
              const status = getPatientDossierStatus(item as any);
              return (
                <View style={{ alignItems: 'center', justifyContent: 'center' }}>
                  <DossierStatusIndicator status={status} size="small" />
                </View>
              );
            },
          },
          {
            title: t('Nom'),
            key: 'nom',
            sortable: true,
            textStyle: {
              fontSize: FontSize.labelMedium_size,
              fontFamily: FontFamily.labelMedium,
              color: Color.MainNoir,
              fontWeight: '600',
              wordWrap: 'wrap',
              whiteSpace: 'wrap',
            },
            value: (item) => {
              const status = getPatientDossierStatus(item as any);
              return (
                <Text
                  style={{
                    fontSize: FontSize.labelMedium_size,
                    fontFamily: FontFamily.labelMedium,
                    color: status === 'GRIS' ? Color.colorGray_150 : Color.MainNoir,
                    fontWeight: '600',
                    wordWrap: 'wrap',
                    whiteSpace: 'wrap',
                  }}
                >
                  {Formatter.FirstUpper(`${item.nom} ${item.prenom}`)}
                </Text>
              );
            },
          },
          {
            title: t('Date'),
            key: 'birthday',
            sortable: true,
            textStyle: {
              fontSize: FontSize.labelMedium_size,
              fontFamily: FontFamily.labelMedium,
              color: Color.MainNoir,
              fontWeight: '600',
              wordWrap: 'wrap',
              whiteSpace: 'wrap',
            },
            value: (item) => {
              return DateUtils.convertDate(item.birthday);
            },
          },
          {
            title: t('Actions'),
            style: { justifyContent: 'flex-end' },
            key: 'id',
            value: (item) => {
              const isArchived = (item as any).isArchived;
              return (
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    flex: 2,
                    minWidth: 140,
                    justifyContent: 'flex-end',
                    marginRight: -10,
                  }}
                >
                  <IconButton
                    icon="eye"
                    onPress={() => router.push(`/patients/${item.id}`)}
                    iconColor={Color.MainBleu}
                    style={{ margin: 0 }}
                  />
                  <IconButton
                    icon="email-send"
                    onPress={() => router.push(`/patients/${item.id}/send-link`)}
                    iconColor={Color.VertElectrique}
                    style={{ margin: 0 }}
                  />
                  <IconButton
                    icon="pencil"
                    onPress={() => router.push(`/patients/${item.id}/maj`)}
                    iconColor={Color.MainBleu}
                    style={{ margin: 0 }}
                  />
                  <Menu
                    isSimplePressable
                    anchor={
                      <IconButton
                        icon="dots-vertical"
                        iconColor={Color.colorGray_150}
                        style={{ margin: 0 }}
                      />
                    }
                    posX={-17}
                  >
                    <MenuItem
                      title={isArchived ? t('Désarchiver') : t('Archiver')}
                      onPress={() => {
                        handleArchivePatient(item.id, !isArchived);
                      }}
                    />
                    <MenuDivider />
                    <MenuItem
                      title={t('Supprimer')}
                      textStyle={{ color: Color.error }}
                      onPress={() => {
                        Alert.alert(
                          t('Attention'),
                          t('Êtes-vous sûr de vouloir supprimer ce patient ?'),
                          [
                            {
                              text: t('Annuler'),
                              style: 'cancel',
                            },
                            {
                              text: 'OK',
                              onPress: async () => {
                                await deletePatient({
                                  variables: { id: item.id },
                                  fetchPolicy: 'network-only',
                                });
                                setLastChange(new Date().toISOString());
                              },
                            },
                          ]
                        );
                      }}
                    />
                  </Menu>
                </View>
              );
            },
          },
        ]}
        onItemPress={(item) => {
          router.push(`/patients/${item.id}`);
        }}
        emptyStateText={t("Aucun patient n'a été trouvé")}
        emptyStateLinkText={t('Ajouter un patient')}
        emptyStateLinkHref={'/patients/ajouter'}
      />
    </ScreenMargin>
  );
};

export default PatientsIndexPage;

const styles = StyleSheet.create({
  icon: {
    height: 22,
    width: 22,
    opacity: 0.3,
  },
  iconContainer: {
    padding: Padding.p_mini,
    height: 48,
    width: 48,
    backgroundColor: Color.BlancCasse,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: Border.br_11xl,
    flexDirection: 'row',
    marginLeft: 12,
  },
  // Styles pour les cartes mobile
  cardContainer: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  card: {
    backgroundColor: Color.blanc,
    borderRadius: Border.br_xs,
    borderWidth: 1,
    borderColor: Color.colorGray_100,
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardLoading: {
    opacity: 0.6,
  },
  statusBadge: {
    width: 4,
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
  },
  cardContent: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 12,
    paddingLeft: 16,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardInfo: {
    flex: 1,
    marginRight: 8,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Color.MainNoir,
    fontFamily: FontFamily.labelMedium,
  },
  cardSubtitle: {
    fontSize: 13,
    color: Color.MainGris,
    fontFamily: FontFamily.labelRegular,
    marginTop: 2,
  },
  cardStatus: {
    fontSize: 12,
    fontWeight: '500',
    fontFamily: FontFamily.labelMedium,
    marginTop: 4,
  },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: Border.br_11xl,
    marginLeft: 12,
  },
});
